import { chosenHue } from '@recipes/domain';
import { Badge, Button, Card, EmptyState, Spinner, cn } from '@recipes/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FolderHeart, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { HueDot } from '@/features/appearance/hue';
import { HUE_SURFACE } from '@/features/appearance/hue-classes';
import { CollectionFormDialog } from '@/features/collections/collection-form-dialog';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { Alert } from '@recipes/ui';

export function CollectionsPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const list = useInfiniteQuery({
    queryKey: queryKeys.collections.all,
    queryFn: ({ pageParam }) => api.collections.list({ cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <PageContainer>
      <FeaturePageHeader
        title={t('collections.title')}
        description={t('collections.description')}
        actions={
          <Button onClick={() => setCreateOpen(true)} data-testid="collection-create">
            <Plus aria-hidden /> {t('collections.create')}
          </Button>
        }
      />
      {list.isLoading && <Spinner label={t('app.loading')} />}
      {list.error && <Alert variant="destructive">{describeError(list.error)}</Alert>}
      {!list.isLoading && items.length === 0 && (
        <EmptyState
          icon={<FolderHeart />}
          title={t('collections.emptyTitle')}
          description={t('collections.emptyBody')}
          action={<Button onClick={() => setCreateOpen(true)}>{t('collections.create')}</Button>}
        />
      )}
      <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {items.map((c) => (
          <li key={c.id}>
            <Card className="h-full overflow-hidden">
              <Link
                to={`/collections/${c.id}`}
                className="block h-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {c.coverUrl ? (
                  <img
                    src={c.coverUrl}
                    alt=""
                    className="aspect-[4/3] w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={cn(
                      'flex aspect-[4/3] items-center justify-center',
                      HUE_SURFACE[chosenHue(c.color, c.name)],
                    )}
                    data-testid="collection-placeholder"
                    aria-hidden
                  >
                    <FolderHeart className="size-8" />
                  </div>
                )}
                <div className="p-3">
                  <h2 className="flex items-start gap-1.5 text-sm font-semibold">
                    <HueDot hue={chosenHue(c.color, c.name)} className="mt-1.5" />
                    <span className="line-clamp-2">{c.name}</span>
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{t('collections.recipeCount', { count: c.recipeCount })}</span>
                    {c.householdId && (
                      <Badge variant="muted">
                        <Users className="mr-1 size-3" aria-hidden /> {t('shared.household.shared')}
                      </Badge>
                    )}
                  </div>
                </div>
              </Link>
            </Card>
          </li>
        ))}
      </ul>
      {list.hasNextPage && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}
      <CollectionFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </PageContainer>
  );
}
